'use client';

import { useEffect } from 'react';
import { useAtomValue } from 'jotai';
import { getFormData } from '@nf/utils-common/form';
import { useQuery } from '@tanstack/react-query';
import queryKeys from '@/constants/query-keys';
import { ApiQueryBasePromise } from '@/types';
import { fetchComposer } from '@nf/utils-common/compose-fetch';
import { getResponseJwtHeader } from '@/utils/web';
import { HistoryResult, SearchResult } from '@/features/search/component/types';
import { useSiteStore } from '@/store/site';
import { getAuthToken, useAuthToken, useAuthActions } from '@/store/auth';
import { ApiDomain } from '@nf/types/site';
import { visitorAtom } from '@/atoms/visitor';
import { cloudUrlParser } from '@/utils/aws-domain-util';
import { useGetSiteConfigs } from './use-site-configs';

interface AutocompletePayload {
	userlanguage?: string
}

const fetchGetAutocomplete = async (
	apiDomain: ApiDomain, 
	token: string | undefined, 
	formData: AutocompletePayload
): Promise<ApiQueryBasePromise<Array<string>>> => {
	const apiUrl = cloudUrlParser(`${apiDomain.ctcdDomain}/Search/GetAutocomplete`);
	const response = await fetchComposer.postWithBearerBase(apiUrl.toString(), {
		body: getFormData(formData)
	})(token);

	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}

	return {
		data: await response.json(),
		jwtToken: getResponseJwtHeader(response)
	};
};

export const useGetAutocomplete = (formData: AutocompletePayload) => {
	const authToken = useAuthToken();
	const visitorInfo = useAtomValue(visitorAtom);
	const token = authToken ?? visitorInfo?.AccessToken;
	const apiDomain = useSiteStore(state => state.apiDomain);
	
	const { data, isLoading, isError, error, refetch } = useQuery({
		queryKey: [queryKeys.SEARCH_AUTOCOMPLETE, token, formData],
		queryFn: () => fetchGetAutocomplete(apiDomain, token, formData),
		cacheTime: 0,
		refetchOnWindowFocus: false,
		enabled: Boolean(token && apiDomain.ctcdDomain)
	});
	const { updateAuthToken } = useAuthActions();

	useEffect(() => {
		const updateJwt = () => {
			if (data?.jwtToken) {
				updateAuthToken(data.jwtToken);
			}
		};

		updateJwt();
	}, [data?.jwtToken]);

	return {
		data: data?.data,
		isLoading,
		isError,
		error,
		refetch
	};
};
interface GetSearchResultPayload {
	userlanguage?: string;
	searchinput?: string;
}

export const fetchGetSearchResult = async (apiDomain: ApiDomain, formData: GetSearchResultPayload, authToken: string | undefined): Promise<ApiQueryBasePromise<Array<SearchResult>>> => {
	const apiUrl = cloudUrlParser(`${apiDomain.ctcdDomain}/Search/GetSearchResult`);
	const response = await fetchComposer.postWithBearerBase(apiUrl.toString(), {
		body: getFormData(formData)
	})(authToken);

	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}
	return {
		data: await response.json(),
		jwtToken: getResponseJwtHeader(response)
	};
};
interface SearchHistoryPayload {
	userlanguage?: string
}
const fetchGetSearchHistory = async (
	apiDomain: ApiDomain, 
	formData: SearchHistoryPayload
): Promise<ApiQueryBasePromise<Array<HistoryResult>>> => {
	const authToken = getAuthToken();
	const apiUrl = cloudUrlParser(`${apiDomain.ctcdDomain}/Search/History`);
	const response = await fetchComposer.postWithBearerBase(apiUrl.toString(), {
		body: getFormData(formData)
	})(authToken);

	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}

	return {
		data: await response.json(),
		jwtToken: getResponseJwtHeader(response)
	};
};

export const useGetSearchHistory = (formData: AutocompletePayload) => {
	const authToken = useAuthToken();
	const apiDomain = useSiteStore(state => state.apiDomain);
	const { SiteConfigsData } = useGetSiteConfigs();

	const { data, isLoading, isError, error, refetch } = useQuery({
		queryKey: [queryKeys.SEARCH_HISTORY, formData],
		queryFn: () => fetchGetSearchHistory(apiDomain, formData),
		cacheTime: 0,
		refetchOnWindowFocus: false,
		enabled: Boolean(authToken && apiDomain.ctcdDomain) && SiteConfigsData?.EnableHistorySearch
	});
	const { updateAuthToken } = useAuthActions();

	useEffect(() => {
		const updateJwt = () => {
			if (data?.jwtToken) {
				updateAuthToken(data.jwtToken);
			}
		};

		updateJwt();
	}, [data?.jwtToken]);

	return {
		data: data?.data,
		isLoading,
		isError,
		error,
		refetch
	};
};
interface PostRemoveHistoryPayload {
	keywordId?: string;
	userlanguage?: string
}
export const fetchPostRemoveHistory = async (apiDomain: ApiDomain, formData: PostRemoveHistoryPayload): Promise<ApiQueryBasePromise<Array<HistoryResult>>> => {
	const authToken = getAuthToken();
	const apiUrl = cloudUrlParser(`${apiDomain.ctcdDomain}/Search/RemoveHistory`);
	const response = await fetchComposer.postWithBearerBase(apiUrl.toString(), {
		body: getFormData(formData)
	})(authToken);

	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}
	return {
		data: await response.json(),
		jwtToken: getResponseJwtHeader(response)
	};
};
